import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserName, useUserRol, useTienePermiso } from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormData {
  fecha: string
  hora: string
  grados: string
  hr: string
  sinEliminarMateria: boolean
  hilosUr: string
  hilosTr: string
  longitudUr: string
  longitudTr: string
  gramosUr: string
  gramosTr: string
  noTexUr: string
  noTexTr: string
  otroUr: string
  otroTr: string
  realizo: string
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean
}

interface NMXA2755FormProps {
  pedidoNumero: string
  pedidoRowId?: string
  onBack: () => void
  onSave: (data: FormData) => void
  realizoUser?: string
  initialData?: Partial<FormData>
  mode?: 'create' | 'view'
  onRefreshBackend?: () => void // <-- NUEVO
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA2755Form({ pedidoNumero, pedidoRowId, onBack, onSave, realizoUser, initialData = {}, mode = 'create', onRefreshBackend, onAutosave }: NMXA2755FormProps) {
  const globalRol = useUserRol();
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = globalRol;

  const [formData, setFormData] = useState<FormData>({
    fecha: new Date().toISOString().split('T')[0],
    hora: new Date().toTimeString().split(' ')[0].substring(0, 5),
    grados: "",
    hr: "",
    sinEliminarMateria: true, // <-- SIEMPRE MARCADA AL INICIO
    hilosUr: "",
    hilosTr: "",
    longitudUr: "",
    longitudTr: "",
    gramosUr: "",
    gramosTr: "",
    noTexUr: "",
    noTexTr: "",
    otroUr: "",
    otroTr: "",
    realizo: (mode === 'create' && realizoUser) ? realizoUser : initialData.realizo || "",
    superviso: "",
    observaciones: "",
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '');
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  useEffect(() => {
    // Convertir SI/NO del backend a boolean para el frontend
    const processedInitialData = { ...initialData };
    if (processedInitialData.sinEliminarMateria === 'SI') {
      processedInitialData.sinEliminarMateria = true as any;
    } else if (processedInitialData.sinEliminarMateria === 'NO') {
      processedInitialData.sinEliminarMateria = false as any;
    }

    // Preservar la lógica correcta para el campo realizo
    const realizoValue = (mode === 'create' && realizoUser) ? realizoUser : initialData.realizo || "";

    setFormData(prev => ({
      ...prev,
      ...processedInitialData,
      realizo: realizoValue,
      mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
    }))
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData, mode, realizoUser])



  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  // Misma lógica que AnalisisQuimicoForm: solo readOnly en modo 'view'
  const isReadOnly = mode === 'view' && !editMode;

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const formPayload = () => ({
    ...formData,
    sinEliminarMateria: formData.sinEliminarMateria ? 'SI' : 'NO'
  })

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      // Convertir el boolean sinEliminarMateria a SI/NO para el backend
      const processedData = formPayload();
      onSave(processedData)
    } finally {
      setGuardando(false)
    }
  }

  // --- ADMIN ACTIONS ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId || (formData as any)?.ROW_ID || '';
  const nomId = autosaveNomId;
  const pedidoValue = (formData as any).PEDIDO || pedidoNumero || '';
  const pruebaValue = (formData as any).PRUEBA || 'NMX-A-275/5-INNTEX-2000 DENSIDAD LINEAL DE HILOS EXTRAIDOS DE LA TELA (METODO A)';

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-275/5-INNTEX-2000 DENSIDAD LINEAL DE HILOS EXTRAIDOS DE LA TELA (METODO A)',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-275-5?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-275-5?';
    if (!window.confirm(confirmMsg)) return;
    // Convertir el boolean sinEliminarMateria a SI/NO para el backend
    const processedData = {
      ...formData,
      sinEliminarMateria: formData.sinEliminarMateria ? 'SI' : 'NO'
    };
    const payload: any = {
      ...processedData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion
    };
    if (accion === 'rechazado' && razonRechazo) {
      payload.RAZON_RECHAZO = razonRechazo;
    }
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? payload.RAZON_RECHAZO : undefined, payload.superviso, nomId);
      setEstatus(payload.ACEPTAR);
      setEditMode(false);
      setShowRechazoModal(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      if (accion === 'rechazado') setRechazoLoading(false);
    }
  };

  const handleEditar = () => {
    setEditMode(true);
  };

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-275-5? Esta acción no se puede deshacer.')) return;
    // Convertir el boolean sinEliminarMateria a SI/NO para el backend
    const processedData = {
      ...formData,
      sinEliminarMateria: formData.sinEliminarMateria ? 'SI' : 'NO'
    };
    const payload = {
      ...processedData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  // Calculate No. Tex automatically
  useEffect(() => {
    let noTexUr = '';
    let noTexTr = '';
    const gramosUr = parseFloat(formData.gramosUr);
    const longitudUr = parseFloat(formData.longitudUr);
    const hilosUr = parseFloat(formData.hilosUr);
    if (!isNaN(gramosUr) && !isNaN(longitudUr) && !isNaN(hilosUr) && longitudUr !== 0 && hilosUr !== 0) {
      noTexUr = (Math.trunc((gramosUr * 1000 / (longitudUr * hilosUr)) * 100) / 100).toFixed(2);
    }
    const gramosTr = parseFloat(formData.gramosTr);
    const longitudTr = parseFloat(formData.longitudTr);
    const hilosTr = parseFloat(formData.hilosTr);
    if (!isNaN(gramosTr) && !isNaN(longitudTr) && !isNaN(hilosTr) && longitudTr !== 0 && hilosTr !== 0) {
      noTexTr = (Math.trunc((gramosTr * 1000 / (longitudTr * hilosTr)) * 100) / 100).toFixed(2);
    }
    setFormData(prev => ({
      ...prev,
      noTexUr,
      noTexTr,
    }));
  }, [formData.gramosUr, formData.longitudUr, formData.hilosUr, formData.gramosTr, formData.longitudTr, formData.hilosTr]);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <div className="flex flex-col">
          <span className="text-2xl font-bold tracking-tight leading-tight">NMX-A-275/5-INNTEX-2000</span>
          <span className="text-lg text-muted-foreground leading-tight">DETERMINACIÓN DE LA DENSIDAD DE LOS HILOS EXTRAÍDOS DE LA TELA (MÉTODO A)</span>
          <span className="mt-2 text-base text-muted-foreground">Método: <span className="font-semibold">MET-7.2-15</span></span>
          <span className="text-muted-foreground mt-1">Pedido: {pedidoNumero}</span>
        </div>
        <div className="flex gap-2 ml-auto">
          {/* Admin action buttons */}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={handleEditar}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Datos de la Solicitud</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="numero_solicitud">Número de solicitud</Label>
              <Input
                id="numero_solicitud"
                type="text"
                placeholder="Ej. 12345"
                value={(formData as any).numero_solicitud || ''}
                onChange={e => handleInputChange('numero_solicitud' as any, e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="id_termohigrometro">ID de termohigrómetro</Label>
              <Select
                value={(formData as any).id_termohigrometro || undefined}
                onValueChange={value => handleInputChange('id_termohigrometro' as any, value === 'placeholder' ? '' : value)}
                disabled={isReadOnly}
              >
                <SelectTrigger id="id_termohigrometro" className="w-full">
                  <SelectValue placeholder="Seleccione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Seleccione...</SelectItem>
                  <SelectItem value="E-59">E-59</SelectItem>
                  <SelectItem value="E-57">E-57</SelectItem>
                  <SelectItem value="E-56">E-56</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Condiciones de Ensayo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="fecha">FECHA</Label>
              <Input
                id="fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) => handleInputChange('fecha', e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="hora">HORA</Label>
              <Input
                id="hora"
                type="time"
                value={formData.hora}
                onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="grados">°C</Label>
              <Input
                id="grados"
                type="number"
                step="0.1"
                placeholder="20.0"
                value={formData.grados}
                onChange={(e) => handleInputChange('grados', e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="hr">% HR</Label>
              <Input
                id="hr"
                type="number"
                step="0.1"
                placeholder="65.0"
                value={formData.hr}
                onChange={(e) => handleInputChange('hr', e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
              />
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                id="sinEliminarMateria"
                checked={formData.sinEliminarMateria}
                onCheckedChange={(checked) => handleInputChange('sinEliminarMateria', checked === true)}
                disabled={isReadOnly}
              />
              <Label htmlFor="sinEliminarMateria" className="text-sm leading-tight">
                SIN ELIMINAR LA MATERIA NO FIBROSA
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mediciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border p-3 text-left w-1/3"></th>
                  <th className="border border-border p-3 text-center w-1/3">URDIMBRE</th>
                  <th className="border border-border p-3 text-center w-1/3">TRAMA</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border p-3 bg-muted">
                    HILOS TOTALES
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="1"
                      placeholder="0"
                      value={formData.hilosUr}
                      onChange={(e) => handleInputChange('hilosUr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="1"
                      placeholder="0"
                      value={formData.hilosTr}
                      onChange={(e) => handleInputChange('hilosTr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3 bg-muted">
                    LONGITUD TOTAL (m)
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="0.001"
                      placeholder="0.000"
                      value={formData.longitudUr}
                      onChange={(e) => handleInputChange('longitudUr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="0.001"
                      placeholder="0.000"
                      value={formData.longitudTr}
                      onChange={(e) => handleInputChange('longitudTr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3 bg-muted">
                    GRAMOS TOTALES (g)
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="0.0001"
                      placeholder="0.0000"
                      value={formData.gramosUr}
                      onChange={(e) => handleInputChange('gramosUr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="0.0001"
                      placeholder="0.0000"
                      value={formData.gramosTr}
                      onChange={(e) => handleInputChange('gramosTr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
                <tr className="bg-accent">
                  <td className="border border-border p-3 bg-muted">
                    <span className="font-medium">No.Tex</span>
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.noTexUr}
                      // No onChange: auto-calculated
                      className="text-center font-medium"
                      readOnly
                      disabled
                    />
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.noTexTr}
                      // No onChange: auto-calculated
                      className="text-center font-medium"
                      readOnly
                      disabled
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3 bg-muted">
                    OTRO
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      placeholder="Especificar..."
                      value={formData.otroUr}
                      onChange={(e) => handleInputChange('otroUr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className="border border-border p-2">
                    <Input
                      placeholder="Especificar..."
                      value={formData.otroTr}
                      onChange={(e) => handleInputChange('otroTr', e.target.value)}
                      className="text-center"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Personal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="realizo">REALIZÓ</Label>
              <Input
                id="realizo"
                placeholder="Nombre del técnico"
                value={formData.realizo}
                readOnly
                className="bg-muted-foreground/10 cursor-not-allowed"
              />
            </div>
            <div>
              <Label htmlFor="superviso">SUPERVISÓ</Label>
              <Input
                id="superviso"
                placeholder="Nombre del supervisor"
                value={formData.superviso}
                readOnly
                className="bg-muted-foreground/10 cursor-not-allowed"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Observaciones</CardTitle>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Mostrar en Reporte Cliente
              <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
            </span>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Ingrese observaciones adicionales..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              className="min-h-[120px]"
              readOnly={isReadOnly}
              disabled={isReadOnly}
            />
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush}>
          Cancelar
        </Button>
        {/* Botón guardar solo en modo creación y nunca en edición */}
        {mode === 'create' && !editMode && (
          <Button onClick={handleSave} disabled={guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar Formulario'}
          </Button>
        )}
      </div>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}